import { action, computed, makeObservable } from "mobx";
import type { Api } from "../../../core/apis";
import { BaseModel, type BaseModelData } from "../../../core/BaseModel";
import type { Stores } from "../../../core/stores";
import type SuperUserModel from "../../../users/SuperUserModel";

export type BusinessCustomerMailComment = {
  dateCreated: number;
  authorUid: string;
  authorName: string;
  text: string;
};

export type BusinessCustomerMailHistory = {
  dateSent: number;
  templateUsed: string;
};

export enum BusinessCustomerMailStatus {
  NotStarted = "not_started",
  Active = "active",
  Finished = "finished", // all emails sent

  Confirmed = "confirmed",
  Paused = "paused",
  Aborted = "aborted",

  MailgunFailed = "mailgun_failed",
}

export interface BusinessCustomerMailData extends BaseModelData {
  status: BusinessCustomerMailStatus;
  companyName: string;
  email: string;

  templateSuffix: string;

  opened: number;
  clicked: number;

  currentStep: number;
  lastEmailSent: number | null;
  history: BusinessCustomerMailHistory[];
  comments: BusinessCustomerMailComment[];
}

export class BusinessCustomerMailModel extends BaseModel<BusinessCustomerMailData> {
  constructor(stores: Stores, api: Api, data: BusinessCustomerMailData) {
    super(stores, api, data);

    makeObservable(this);

    Object.assign(this.data, {
      templateSuffix: this.data.templateSuffix ?? "",
      clicked: this.data.clicked ?? 0,
      opened: this.data.opened ?? 0,
    });
  }

  @action async addComment(comment: string) {
    const superUser: SuperUserModel | undefined =
      this.stores.sessionStore.superUser;
    if (!superUser) {
      throw Error("no user");
    }

    this.data.comments.push({
      dateCreated: Date.now(),
      authorName: superUser.data.displayName,
      authorUid: superUser.uid,
      text: comment,
    });

    await this.api.businessCustomerMailsApi.update(this.uid, {
      comments: this.data.comments,
    });
  }

  @action async updateStatus(newStatus: BusinessCustomerMailStatus) {
    this.data.status = newStatus;
    await this.api.businessCustomerMailsApi.update(this.uid, {
      status: this.data.status,
    });
  }

  @action async save(data: Partial<BusinessCustomerMailData>) {
    this.data.status = data.status ?? this.data.status;
    this.data.companyName = data.companyName ?? this.data.companyName;
    this.data.email = data.email ?? this.data.email;
    this.data.currentStep = data.currentStep ?? this.data.currentStep;
    this.data.templateSuffix = data.templateSuffix ?? this.data.templateSuffix;

    await this.api.businessCustomerMailsApi.set(this);
  }

  @computed get isActive() {
    return this.data.status === BusinessCustomerMailStatus.Active;
  }

  toFireStore(): Omit<BusinessCustomerMailData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }
}
